import {DateTime} from 'luxon'
import {useCallback} from 'react'

import {PromoApplied, PromoError} from '../../api/promo-codes/types'
import {TranslatorScope} from '../../locale/types'
import {l as translate, useLocale} from '../../utils/i18n'

const KNOWN_ERROR_CODES = [
  'PROMO_NOT_FOUND',
  'PROMO_INACTIVE',
  'PROMO_NOT_STARTED',
  'PROMO_EXPIRED',
  'PROMO_USED_UP',
  'PROMO_CUSTOMER_LIMIT',
  'PROMO_MIN_SPEND',
  'PROMO_NOT_APPLICABLE',
  'PROMO_PRICE_CHANGED',
  'PROMO_SIGN_IN',
  'PROMO_NEW_ONLY',
  'PROMO_RATE_LIMITED',
]

const formatPromoDate = (value?: string | null) => {
  if (!value) {
    return ''
  }
  const date = DateTime.fromISO(value)
  return date.isValid ? date.toFormat('d LLL yyyy') : value
}

// "1,000" / "1,234.5": grouped thousands, at most two decimals, so a min-spend reads like a price.
const groupNumber = (value: number) =>
  Number.isFinite(value) ? value.toLocaleString('en-US', {maximumFractionDigits: 2}) : String(value)

export const formatPromoMoney = (value: number, currency?: string | null) =>
  currency ? translate(`${currency}_symbolic` as TranslatorScope, {value: groupNumber(value)}) : groupNumber(value)

export const getPromoErrorMessage = (
  error: PromoError | null | undefined,
  fallbackCurrency?: string | null,
  language?: string,
) => {
  if (!error) {
    return ''
  }
  const serverText = error.message?.trim()
  // The server's own friendly sentence (validate answers carry one) says the most, e.g. what the
  // code does work on; it is English, so other languages keep their translated sentence.
  if (error.friendly && serverText && (!language || language === 'en')) {
    return serverText
  }
  if (!KNOWN_ERROR_CODES.includes(error.code)) {
    return serverText || translate('promo_error_fallback')
  }
  if (error.code === 'PROMO_NOT_APPLICABLE' && error.meta?.reason === 'NOTHING_TO_DISCOUNT') {
    return translate('promo_error_PROMO_NOT_APPLICABLE_FREE')
  }
  return translate(`promo_error_${error.code}` as TranslatorScope, {
    date: formatPromoDate(error.meta?.validFrom || error.meta?.validUntil),
    amount: formatPromoMoney(Number(error.meta?.minSpend || 0), error.meta?.currency || fallbackCurrency),
  })
}

export const usePromoText = (fallbackCurrency?: string | null) => {
  const {l, language} = useLocale()

  const money = useCallback(
    (value: number, currency?: string | null) => formatPromoMoney(value, currency || fallbackCurrency),
    [fallbackCurrency, language],
  )

  const errorMessage = useCallback(
    (error: PromoError | null | undefined) => getPromoErrorMessage(error, fallbackCurrency, language),
    [fallbackCurrency, language],
  )

  // "15%" or "฿200" — the size of the discount, without the word "off".
  const discountValue = useCallback(
    (applied: Pick<PromoApplied, 'discount' | 'discountType'>) =>
      applied.discountType === 'PERCENT' ? `${applied.discount}%` : money(applied.discount),
    [money],
  )

  const discountLabel = useCallback(
    (applied: Pick<PromoApplied, 'discount' | 'discountType'>) =>
      applied.discountType === 'PERCENT'
        ? l('promo_percent_off', {value: applied.discount})
        : l('promo_amount_off', {amount: money(applied.discount)}),
    [l, money],
  )

  return {errorMessage, discountLabel, discountValue, money}
}
